import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Trash2, GripVertical, Image, Type, Users, Mail } from "lucide-react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

export interface ContentBlock {
  id: string;
  type: 'hero' | 'benefits' | 'testimonials' | 'contact' | 'custom';
  title: string;
  content: any;
  settings: {
    backgroundColor?: string;
    textColor?: string;
    padding?: string;
    margin?: string;
    isVisible?: boolean;
  };
  order: number;
}

interface ContentEditorProps {
  contentData: any;
  onUpdate: (updatedContent: any) => void;
}

const BLOCK_TYPES = [
  { value: 'hero', label: 'Hero Section', icon: Type, description: 'Hauptbereich mit Titel und Call-to-Action' },
  { value: 'benefits', label: 'Vorteile', icon: Users, description: 'Liste der Vorteile und Features' },
  { value: 'testimonials', label: 'Testimonials', icon: Users, description: 'Kundenbewertungen und Referenzen' },
  { value: 'contact', label: 'Kontaktformular', icon: Mail, description: 'Lead-Erfassungsformular' },
  { value: 'custom', label: 'Custom Block', icon: Plus, description: 'Benutzerdefinierter Inhalt' }
];

export default function ContentEditor({ contentData, onUpdate }: ContentEditorProps) {
  const [blocks, setBlocks] = useState<ContentBlock[]>([]);
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);

  useEffect(() => {
    // Initialize blocks from contentData
    if (contentData?.blocks) {
      setBlocks(contentData.blocks);
    } else {
      // Create default blocks
      const defaultBlocks: ContentBlock[] = [
        {
          id: 'hero-1',
          type: 'hero',
          title: 'Hero Section',
          content: {
            title: contentData?.title || 'Willkommen bei {tenant_name}',
            subtitle: contentData?.subtitle || 'Entdecken Sie unsere exklusiven Angebote',
            buttonText: 'Jetzt entdecken',
            buttonLink: '#contact',
            backgroundImage: contentData?.backgroundImage || null
          },
          settings: {
            backgroundColor: '#ffffff',
            textColor: '#000000',
            padding: 'py-20',
            isVisible: true
          },
          order: 0
        },
        {
          id: 'benefits-1',
          type: 'benefits',
          title: 'Vorteile',
          content: {
            title: 'Warum {tenant_name} wählen?',
            benefits: contentData?.benefits || [
              { title: 'Hochwertige Qualität', description: 'Nur die besten Produkte und Services' },
              { title: 'Persönlicher Service', description: 'Individuelle Beratung für jeden Kunden' },
              { title: 'Faire Preise', description: 'Transparente Preisgestaltung ohne versteckte Kosten' }
            ]
          },
          settings: {
            backgroundColor: '#f8f9fa',
            textColor: '#000000',
            padding: 'py-16',
            isVisible: true
          },
          order: 1
        },
        {
          id: 'contact-1',
          type: 'contact',
          title: 'Kontaktformular',
          content: {
            title: 'Interesse geweckt?',
            subtitle: 'Kontaktieren Sie uns noch heute!',
            fields: [
              { name: 'first_name', label: 'Vorname', type: 'text', required: true },
              { name: 'last_name', label: 'Nachname', type: 'text', required: true },
              { name: 'email', label: 'E-Mail', type: 'email', required: true },
              { name: 'phone', label: 'Telefon', type: 'tel', required: false },
              { name: 'message', label: 'Nachricht', type: 'textarea', required: false }
            ],
            buttonText: 'Anfrage senden'
          },
          settings: {
            backgroundColor: '#ffffff',
            textColor: '#000000',
            padding: 'py-16',
            isVisible: true
          },
          order: 2
        }
      ];
      setBlocks(defaultBlocks);
    }
  }, [contentData]);

  const handleAddBlock = (type: ContentBlock['type']) => {
    const newBlock: ContentBlock = {
      id: `${type}-${Date.now()}`,
      type,
      title: BLOCK_TYPES.find(bt => bt.value === type)?.label || 'New Block',
      content: getDefaultContentForType(type),
      settings: {
        backgroundColor: '#ffffff',
        textColor: '#000000',
        padding: 'py-16',
        isVisible: true
      },
      order: blocks.length
    };
    
    const updatedBlocks = [...blocks, newBlock];
    setBlocks(updatedBlocks);
    updateContentData(updatedBlocks);
  };

  const handleDeleteBlock = (blockId: string) => {
    const updatedBlocks = blocks.filter(block => block.id !== blockId);
    setBlocks(updatedBlocks);
    updateContentData(updatedBlocks);
    
    if (selectedBlockId === blockId) {
      setSelectedBlockId(null);
    }
  };

  const handleUpdateBlock = (blockId: string, updates: Partial<ContentBlock>) => {
    const updatedBlocks = blocks.map(block => 
      block.id === blockId ? { ...block, ...updates } : block
    );
    setBlocks(updatedBlocks);
    updateContentData(updatedBlocks);
  };

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    const reorderedBlocks = Array.from(blocks);
    const [movedBlock] = reorderedBlocks.splice(result.source.index, 1);
    reorderedBlocks.splice(result.destination.index, 0, movedBlock);

    // Update order
    const updatedBlocks = reorderedBlocks.map((block, index) => ({
      ...block,
      order: index
    }));

    setBlocks(updatedBlocks);
    updateContentData(updatedBlocks);
  };

  const updateContentData = (updatedBlocks: ContentBlock[]) => {
    const newContentData = {
      ...contentData,
      blocks: updatedBlocks,
      lastModified: new Date().toISOString()
    };
    onUpdate(newContentData);
  };

  const getDefaultContentForType = (type: ContentBlock['type']) => {
    switch (type) {
      case 'hero':
        return {
          title: 'Neuer Hero Bereich',
          subtitle: 'Beschreibung hier eingeben',
          buttonText: 'Call-to-Action',
          buttonLink: '#contact'
        };
      case 'benefits':
        return {
          title: 'Unsere Vorteile',
          benefits: [
            { title: 'Vorteil 1', description: 'Beschreibung des ersten Vorteils' },
            { title: 'Vorteil 2', description: 'Beschreibung des zweiten Vorteils' }
          ]
        };
      case 'testimonials':
        return {
          title: 'Was unsere Kunden sagen',
          testimonials: [
            { name: 'Max Mustermann', text: 'Großartiger Service!', rating: 5 }
          ]
        };
      case 'contact':
        return {
          title: 'Kontakt aufnehmen',
          subtitle: 'Wir freuen uns auf Ihre Nachricht',
          fields: [
            { name: 'first_name', label: 'Vorname', type: 'text', required: true },
            { name: 'email', label: 'E-Mail', type: 'email', required: true }
          ],
          buttonText: 'Senden'
        };
      case 'custom':
        return {
          html: '<div><h2>Custom Content</h2><p>Fügen Sie hier Ihren benutzerdefinierten Inhalt hinzu.</p></div>'
        };
      default:
        return {};
    }
  };

  const selectedBlock = blocks.find(block => block.id === selectedBlockId);

  return (
    <div className="grid lg:grid-cols-3 gap-6 h-full">
      {/* Block List */}
      <div className="lg:col-span-1 space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="font-semibold">Content Blocks</h3>
          <Select onValueChange={(value) => handleAddBlock(value as ContentBlock['type'])}>
            <SelectTrigger className="w-auto">
              <Plus className="h-4 w-4" />
            </SelectTrigger>
            <SelectContent>
              {BLOCK_TYPES.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  <div className="flex items-center gap-2">
                    <type.icon className="h-4 w-4" />
                    <div>
                      <div className="font-medium">{type.label}</div>
                      <div className="text-xs text-muted-foreground">{type.description}</div>
                    </div>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="blocks">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-2">
                {blocks
                  .sort((a, b) => a.order - b.order)
                  .map((block, index) => (
                    <Draggable key={block.id} draggableId={block.id} index={index}>
                      {(provided, snapshot) => (
                        <Card
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className={`cursor-pointer transition-all ${
                            selectedBlockId === block.id ? 'ring-2 ring-primary' : ''
                          } ${snapshot.isDragging ? 'shadow-lg' : ''}`}
                          onClick={() => setSelectedBlockId(block.id)}
                        >
                          <CardHeader className="pb-2">
                            <div className="flex items-center gap-2">
                              <div {...provided.dragHandleProps}>
                                <GripVertical className="h-4 w-4 text-muted-foreground" />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <CardTitle className="text-sm">{block.title}</CardTitle>
                                  <Badge variant="outline" className="text-xs">
                                    {BLOCK_TYPES.find(t => t.value === block.type)?.label}
                                  </Badge>
                                </div>
                              </div>
                              <div className="flex items-center gap-1">
                                <Switch
                                  checked={block.settings.isVisible}
                                  onCheckedChange={(checked) => 
                                    handleUpdateBlock(block.id, {
                                      settings: { ...block.settings, isVisible: checked }
                                    })
                                  }
                                />
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteBlock(block.id);
                                  }}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          </CardHeader>
                        </Card>
                      )}
                    </Draggable>
                  ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>

      {/* Block Editor */}
      <div className="lg:col-span-2">
        {selectedBlock ? (
          <BlockEditor
            block={selectedBlock}
            onUpdate={(updates) => handleUpdateBlock(selectedBlock.id, updates)}
          />
        ) : (
          <Card>
            <CardContent className="text-center py-8">
              <p className="text-muted-foreground">
                Wählen Sie einen Block aus der Liste aus, um ihn zu bearbeiten.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

interface BlockEditorProps {
  block: ContentBlock;
  onUpdate: (updates: Partial<ContentBlock>) => void;
}

function BlockEditor({ block, onUpdate }: BlockEditorProps) {
  const handleContentChange = (field: string, value: any) => {
    onUpdate({
      content: {
        ...block.content,
        [field]: value
      }
    });
  };

  const handleSettingsChange = (field: string, value: any) => {
    onUpdate({
      settings: {
        ...block.settings,
        [field]: value
      }
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <span>{block.title} bearbeiten</span>
          <Badge>{BLOCK_TYPES.find(t => t.value === block.type)?.label}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="content">
          <TabsList>
            <TabsTrigger value="content">Inhalt</TabsTrigger>
            <TabsTrigger value="design">Design</TabsTrigger>
          </TabsList>
          
          <TabsContent value="content" className="space-y-4">
            {block.type === 'hero' && (
              <HeroEditor content={block.content} onChange={handleContentChange} />
            )}
            {block.type === 'benefits' && (
              <BenefitsEditor content={block.content} onChange={handleContentChange} />
            )}
            {block.type === 'contact' && (
              <ContactEditor content={block.content} onChange={handleContentChange} />
            )}
            {block.type === 'testimonials' && (
              <TestimonialsEditor content={block.content} onChange={handleContentChange} />
            )}
            {block.type === 'custom' && (
              <CustomEditor content={block.content} onChange={handleContentChange} />
            )}
          </TabsContent>
          
          <TabsContent value="design" className="space-y-4">
            <DesignEditor settings={block.settings} onChange={handleSettingsChange} />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}

// Hero Block Editor
function HeroEditor({ content, onChange }: { content: any; onChange: (field: string, value: any) => void }) {
  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="hero-title">Titel</Label>
        <Input
          id="hero-title"
          value={content.title || ''}
          onChange={(e) => onChange('title', e.target.value)}
          placeholder="z.B. Willkommen bei {tenant_name}"
        />
      </div>
      
      <div>
        <Label htmlFor="hero-subtitle">Untertitel</Label>
        <Textarea
          id="hero-subtitle"
          value={content.subtitle || ''}
          onChange={(e) => onChange('subtitle', e.target.value)}
          placeholder="Beschreibung oder Slogan"
          rows={3}
        />
      </div>
      
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="hero-button-text">Button Text</Label>
          <Input
            id="hero-button-text"
            value={content.buttonText || ''}
            onChange={(e) => onChange('buttonText', e.target.value)}
            placeholder="z.B. Jetzt entdecken"
          />
        </div>
        
        <div>
          <Label htmlFor="hero-button-link">Button Link</Label>
          <Input
            id="hero-button-link"
            value={content.buttonLink || ''}
            onChange={(e) => onChange('buttonLink', e.target.value)}
            placeholder="z.B. #contact oder https://..."
          />
        </div>
      </div>
      
      <div>
        <Label htmlFor="hero-bg-image">Hintergrundbild URL</Label>
        <Input
          id="hero-bg-image"
          value={content.backgroundImage || ''}
          onChange={(e) => onChange('backgroundImage', e.target.value)}
          placeholder="https://example.com/image.jpg"
        />
      </div>
    </div>
  );
}

// Benefits Editor
function BenefitsEditor({ content, onChange }: { content: any; onChange: (field: string, value: any) => void }) {
  const benefits = content.benefits || [];
  
  const addBenefit = () => {
    const newBenefits = [...benefits, { title: 'Neuer Vorteil', description: 'Beschreibung hier eingeben' }];
    onChange('benefits', newBenefits);
  };
  
  const updateBenefit = (index: number, field: string, value: string) => {
    const newBenefits = benefits.map((benefit: any, i: number) => 
      i === index ? { ...benefit, [field]: value } : benefit
    );
    onChange('benefits', newBenefits);
  };
  
  const removeBenefit = (index: number) => {
    const newBenefits = benefits.filter((_: any, i: number) => i !== index);
    onChange('benefits', newBenefits);
  };
  
  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="benefits-title">Titel</Label>
        <Input
          id="benefits-title"
          value={content.title || ''}
          onChange={(e) => onChange('title', e.target.value)}
          placeholder="z.B. Unsere Vorteile"
        />
      </div>
      
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <Label>Vorteile</Label>
          <Button variant="outline" size="sm" onClick={addBenefit}>
            <Plus className="h-4 w-4 mr-2" />
            Vorteil hinzufügen
          </Button>
        </div>
        
        {benefits.map((benefit: any, index: number) => (
          <Card key={index}>
            <CardContent className="pt-4">
              <div className="space-y-3">
                <div className="flex justify-between items-start">
                  <Label>Vorteil {index + 1}</Label>
                  <Button variant="ghost" size="sm" onClick={() => removeBenefit(index)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                
                <Input
                  value={benefit.title || ''}
                  onChange={(e) => updateBenefit(index, 'title', e.target.value)}
                  placeholder="Titel des Vorteils"
                />
                
                <Textarea
                  value={benefit.description || ''}
                  onChange={(e) => updateBenefit(index, 'description', e.target.value)}
                  placeholder="Beschreibung des Vorteils"
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

// Contact Form Editor
function ContactEditor({ content, onChange }: { content: any; onChange: (field: string, value: any) => void }) {
  const fields = content.fields || [];
  
  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="contact-title">Titel</Label>
        <Input
          id="contact-title"
          value={content.title || ''}
          onChange={(e) => onChange('title', e.target.value)}
          placeholder="z.B. Kontakt aufnehmen"
        />
      </div>
      
      <div>
        <Label htmlFor="contact-subtitle">Untertitel</Label>
        <Input
          id="contact-subtitle"
          value={content.subtitle || ''}
          onChange={(e) => onChange('subtitle', e.target.value)}
          placeholder="z.B. Wir freuen uns auf Ihre Nachricht"
        />
      </div>
      
      <div>
        <Label htmlFor="contact-button">Button Text</Label>
        <Input
          id="contact-button"
          value={content.buttonText || ''}
          onChange={(e) => onChange('buttonText', e.target.value)}
          placeholder="z.B. Anfrage senden"
        />
      </div>
      
      <div>
        <Label>Formularfelder</Label>
        <div className="mt-2 space-y-2">
          {fields.map((field: any, index: number) => (
            <div key={index} className="flex items-center gap-2 p-2 border rounded">
              <span className="text-sm font-medium">{field.label}</span>
              <Badge variant="outline">{field.type}</Badge>
              {field.required && <Badge variant="secondary">Pflichtfeld</Badge>}
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          Formularfelder werden automatisch basierend auf dem Template erstellt.
        </p>
      </div>
    </div>
  );
}

// Testimonials Editor
function TestimonialsEditor({ content, onChange }: { content: any; onChange: (field: string, value: any) => void }) {
  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="testimonials-title">Titel</Label>
        <Input
          id="testimonials-title"
          value={content.title || ''}
          onChange={(e) => onChange('title', e.target.value)}
          placeholder="z.B. Was unsere Kunden sagen"
        />
      </div>
      
      <div className="bg-muted p-4 rounded-lg">
        <p className="text-sm text-muted-foreground">
          Testimonials-Management wird in der nächsten Version verfügbar sein.
        </p>
      </div>
    </div>
  );
}

// Custom HTML Editor
function CustomEditor({ content, onChange }: { content: any; onChange: (field: string, value: any) => void }) {
  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="custom-html">HTML Inhalt</Label>
        <Textarea
          id="custom-html"
          value={content.html || ''}
          onChange={(e) => onChange('html', e.target.value)}
          placeholder="<div><h2>Ihr Custom Content</h2><p>HTML Code hier eingeben...</p></div>"
          rows={10}
          className="font-mono text-sm"
        />
        <p className="text-xs text-muted-foreground mt-1">
          Sie können HTML, CSS und einfaches JavaScript verwenden.
        </p>
      </div>
    </div>
  );
}

// Design Settings Editor
function DesignEditor({ settings, onChange }: { settings: any; onChange: (field: string, value: any) => void }) {
  return (
    <div className="space-y-4">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="bg-color">Hintergrundfarbe</Label>
          <Input
            id="bg-color"
            type="color"
            value={settings.backgroundColor || '#ffffff'}
            onChange={(e) => onChange('backgroundColor', e.target.value)}
          />
        </div>
        
        <div>
          <Label htmlFor="text-color">Textfarbe</Label>
          <Input
            id="text-color"
            type="color"
            value={settings.textColor || '#000000'}
            onChange={(e) => onChange('textColor', e.target.value)}
          />
        </div>
      </div>
      
      <div>
        <Label htmlFor="padding">Innenabstand</Label>
        <Select value={settings.padding || 'py-16'} onValueChange={(value) => onChange('padding', value)}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="py-8">Klein (py-8)</SelectItem>
            <SelectItem value="py-12">Mittel (py-12)</SelectItem>
            <SelectItem value="py-16">Groß (py-16)</SelectItem>
            <SelectItem value="py-20">Extra Groß (py-20)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      <div className="flex items-center space-x-2">
        <Switch
          id="visibility"
          checked={settings.isVisible !== false}
          onCheckedChange={(checked) => onChange('isVisible', checked)}
        />
        <Label htmlFor="visibility">Block sichtbar</Label>
      </div>
    </div>
  );
}